import MedicineDictionaryModel from '@/entities/medicine-dictionary/model/medicineDictionary.model';
import { createMedicineDictionaryDetailUrlWithKeyword } from '@/entities/medicine-dictionary/util';
import useCustomRouter from '@/hooks/useCustomRouter';
import useNavigate from '@/hooks/useNavigate';
import { ROUTES } from '@/types/common';
import { EIntegratedSearchTabType, ERequestButtonType } from '@/types/search';
import { coreLogger } from '@/utils/logger';
import useGetKeywordSearchMedicineDictionaryInfinite from './useGetKeywordSearchMedicineDictionaryInfinite';

interface Props {
  keyword: string;
  tab: EIntegratedSearchTabType;
}

export interface SearchMedicineDictionaryViewModel {
  keyword: string;
  medicineDictionaries: Array<MedicineDictionaryModel>;
  isMedicineDictionariesLoading: boolean;
  hasMedicineDictionariesNextPage: boolean | undefined;
  fetchMedicineDictionariesNextPage: () => void;
  onClickMedicineDictionary: (medicineDictionaryId: string) => () => void;
  onClickLogMedicineDictionary: (medicineDictionaryId: string) => void;
  handleClickRequestButton: () => void;
}

const useSearchMedicineDictionaryViewModel = ({ keyword, tab }: Props): SearchMedicineDictionaryViewModel => {
  const { pageName, moveToPage } = useCustomRouter();
  const navigate = useNavigate();
  const {
    medicineDictionariesData,
    isMedicineDictionariesLoading,
    hasMedicineDictionariesNextPage,
    fetchMedicineDictionariesNextPage,
  } = useGetKeywordSearchMedicineDictionaryInfinite({
    keyword,
    enabled: tab === EIntegratedSearchTabType.MedicineDictionary,
  });

  const handleClickRequestButton = () => {
    coreLogger({
      eventName: 'request_additional_info_click',
      pageName: pageName,
      payload: {
        position: ERequestButtonType.BOTTOM,
        keyword,
        section_name: 'integrate_search_hospital',
      },
    });
    moveToPage({
      webLink: `${ROUTES.SEARCH(keyword.replaceAll(' ', '+')).REQUEST_INFO.path}`,
      mobileScreenName: ROUTES.SEARCH(keyword.replaceAll(' ', '+')).REQUEST_INFO.appScreenName,
      params: { keyword },
    });
  };

  const onClickLogMedicineDictionary = (medicineDictionaryId: string) => {
    coreLogger({
      eventName: 'search_item_click',
      pageName,
      payload: {
        keyword,
        content_id: medicineDictionaryId,
        tab: EIntegratedSearchTabType.MedicineDictionary,
      },
    });
  };

  const onClickMedicineDictionary = (medicineDictionaryId: string) => () => {
    navigate.navigateMedicineDictionaryDetail({
      url: createMedicineDictionaryDetailUrlWithKeyword(medicineDictionaryId, keyword),
    });
  };

  return {
    keyword,
    medicineDictionaries: medicineDictionariesData?.pages.flatMap((medicineDictionary) => medicineDictionary) ?? [],
    isMedicineDictionariesLoading,
    hasMedicineDictionariesNextPage,
    fetchMedicineDictionariesNextPage,
    onClickMedicineDictionary,
    onClickLogMedicineDictionary,
    handleClickRequestButton,
  };
};

export default useSearchMedicineDictionaryViewModel;
